<!-- eslint-disable max-len -->
<template>
	<div
		class="flex flex-nowrap items-center rounded border bg-white transition-colors duration-75"
		:class="{
			'h-10': type !== 'textarea' && size === 'sm',
			'h-14': type !== 'textarea' && size === 'md',
			'border-red-500 focus-within:border-red-600 hover:border-red-600': error,
			'border-gray-100 focus-within:border-gray-200 hover:border-gray-200':
				!error,
		}"
	>
		<v-icon
			v-if="type === 'search'"
			icon="search"
			class="mx-xs text-2xl md:mx-sm"
		/>

		<div class="flex h-full flex-1">
			<textarea
				v-if="type === 'textarea'"
				:id="id"
				v-model="value"
				:aria-label="label"
				:aria-describedby="ariaDescribedby"
				:aria-invalid="error"
				:name="name"
				class="block size-full bg-transparent px-3 py-1 outline-hidden"
				:class="{
					'text-left': align === 'left',
					'text-right': align === 'right',
					'text-center': align === 'center',
				}"
				:rows="rows"
				:placeholder="placeholder"
				:readonly="readonly"
			/>

			<input
				v-else
				:id="id"
				v-model="value"
				:aria-label="label"
				:aria-invalid="error"
				:aria-describedby="ariaDescribedby"
				:name="name"
				class="ring-focus block size-full bg-transparent px-3 py-1 outline-hidden"
				:class="{
					'text-left': align === 'left',
					'text-right': align === 'right',
					'text-center': align === 'center',
					// eslint-disable-next-line max-len, max-len
					'[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden [&::-webkit-search-results-button]:hidden [&::-webkit-search-results-decoration]:hidden':
						type === 'search',
				}"
				:placeholder="
					typeof placeholder === 'number' ? `${placeholder}` : placeholder
				"
				:type="type"
				:readonly="readonly"
				:autocomplete="autocomplete"
				:pattern="pattern"
				:min="min"
				:max="max"
				:step="step"
			/>

			<label :for="id" class="font-sans text-xs tracking-3xl text-gray-500">
				<p class="mt-2xs mb-sm">{{ label }}</p>
			</label>
		</div>

		<button
			v-if="clearable"
			type="reset"
			class="mr-sm flex size-8 items-center justify-center hover:rounded-full hover:bg-gray-100"
			@click="clear"
		>
			<v-icon class="text-lg" icon="close" />
		</button>
	</div>
</template>

<script setup lang="ts">
const properties = withDefaults(
	defineProps<{
		modelValue?: string | number;
		type?:
			| "text"
			| "textarea"
			| "password"
			| "date"
			| "number"
			| "search"
			| "tel"
			| "email";
		placeholder?: string;
		align?: "left" | "right" | "center";
		size?: "sm" | "md";
		required?: boolean;
		readonly?: boolean;
		error?: boolean;
		id?: string;
		name?: string;
		label?: string;
		pattern?: string;
		autocomplete?: string;
		min?: number;
		max?: number;
		step?: number;
		rows?: number;
		clearable?: boolean;
		ariaDescribedby?: string;
		ariaInvalid?: boolean;
	}>(),
	{
		modelValue: undefined,
		type: "text",
		placeholder: undefined,
		align: "left",
		size: "sm",
		name: undefined,
		label: undefined,
		pattern: undefined,
		autocomplete: undefined,
		min: undefined,
		max: undefined,
		step: undefined,
		rows: 4,
		ariaDescribedby: undefined,
		id: undefined,
	},
);

const emit = defineEmits<{
	(event: "update:modelValue", value: string | number): void;
	(event: "clear"): void;
}>();

const value = computed({
	get() {
		return properties.modelValue || "";
	},
	set(newValue: string | number) {
		emit("update:modelValue", newValue);
	},
});

function clear() {
	emit("update:modelValue", "");
	emit("clear");
}
</script>
